import { useEffect, useState } from "react";
import Icon from "@mdi/react";
import { mdiPlus, mdiTrashCanOutline } from "@mdi/js";
import { useHistory, useLocation } from "react-router-dom";
import { WarehouseModel } from "../../../../../erp/inventory/models/warehouse_model";
import {
  ToastType,
  useToast,
} from "../../../../../context_providers/toast/toast_ctx";
import {
  AddressModel,
  CreateReturnableDCPayload,
  ItemWarehouseModel,
  ReturnableDCConsolidatedResult,
  ReturnableDCSource,
  ReturnableDCSourceType,
} from "../../model/warehouse_outward_model";
import WarehouseSearchField from "../../../../../erp/inventory/warehouse/warehouse_search_field";
import {
  CreateReturnableDC,
  GetItemWAC,
  GetReturnableDCOrdersByCustomerConsolidated,
  GetReturnableDCSource,
} from "../../warehouse_module_repo";
import LoadingWidget from "../../../../../context_providers/modal/loader";
import RequiredMark from "../../../../new_components/common/required_mark";
import UserSearchFieldV2 from "../../../orders/order_create/customer_section/customer/user_search_widgetV2";
import DateRangeFilter from "../../../../new_components/common/responsive/date_range_filter";
import { UserModel } from "../../../../../context_providers/auth/user_model";
import {
  isIndianAddress,
  resolveIndianStateName,
  stateData,
} from "../../../../../utils/states_data";
import {
  matchStateName,
  PincodeHint,
  usePincodeCheck,
} from "../../../../new_components/common/location/usePincodeCheck";

// Pre-select the State dropdown for a source order's address; an
// unrecognised Indian state is cleared so the user has to pick one.
const withResolvedState = (address: AddressModel): AddressModel =>
  isIndianAddress(address.country)
    ? { ...address, state: resolveIndianStateName(address.state) ?? "" }
    : { ...address };

type DCItemRow = {
  itemCode: string;
  itemName: string;
  quantity: number;
  unitPrice: number;
  // Locked when the price came from the source order or WAC; editable only
  // when neither gave a price above 0.
  priceLocked: boolean;
  itemUnit: string;
  itemHsn: string;
  subcategory: string;
  deliveredQty: number; // cap — what the counterparty actually received
  sourceOrderIds: string[];
};

const SOURCE_TYPES: { key: ReturnableDCSourceType; label: string }[] = [
  { key: "SO", label: "Sales Order" },
  { key: "PO", label: "Purchase Order" },
  { key: "ST", label: "Stock Transfer" },
];

const emptyAddress = (): AddressModel => ({
  line1: "",
  line2: "",
  line3: "",
  city: "",
  state: "",
  country: "India",
  pincode: 0,
});

const isFinishedGoods = (subcategory?: string) =>
  (subcategory ?? "").trim().toLowerCase() === "finished goods";

export default function ReverseSTForm(): JSX.Element {
  const history = useHistory();
  const location = useLocation();
  const { showToast } = useToast();

  // Persist the returnable-DC source type in the URL so a refresh restores it.
  const urlSubtype = (
    new URLSearchParams(location.search).get("subtype") ?? ""
  ).toUpperCase();
  const [sourceType, setSourceType] = useState<ReturnableDCSourceType>(
    urlSubtype === "PO" || urlSubtype === "ST"
      ? (urlSubtype as ReturnableDCSourceType)
      : "SO",
  );

  const changeSourceType = (next: ReturnableDCSourceType) => {
    setSourceType(next);
    const params = new URLSearchParams(location.search);
    params.set("subtype", next.toLowerCase());
    history.replace({ search: params.toString() });
  };
  const [orderIdInput, setOrderIdInput] = useState<string>("");
  const [linkedOrderIds, setLinkedOrderIds] = useState<string[]>([]);

  // Alternate entry mode: pick a customer + date range instead of typing
  // individual order ids. Only ever produces SO-type reverse pickups.
  const [entryMode, setEntryMode] = useState<"order" | "customer">("order");
  const [selectedCustomer, setSelectedCustomer] = useState<UserModel>();
  const [customerFromDate, setCustomerFromDate] = useState<string>();
  const [customerToDate, setCustomerToDate] = useState<string>();

  const [items, setItems] = useState<DCItemRow[]>([]);

  const [toWarehouse, setToWarehouse] = useState<WarehouseModel | null>(null);
  const [fromAddress, setFromAddress] = useState<AddressModel>(emptyAddress());

  const [customerId, setCustomerId] = useState<string>("");
  const [customerName, setCustomerName] = useState<string>("");

  // Identity every linked order must share (SO→customer, PO→vendor, ST→warehouse).
  const [matchKey, setMatchKey] = useState<string>("");
  const [matchLabel, setMatchLabel] = useState<string>("");

  const [contactName, setContactName] = useState<string>("");
  const [contactNumber, setContactNumber] = useState<string>("");
  const [purpose, setPurpose] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [logisticPartner, setLogisticPartner] = useState<string>("");
  const [trackingId, setTrackingId] = useState<string>("");

  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(false);

  const sourceLocked = linkedOrderIds.length > 0;
  const totalValue = items.reduce((t, i) => t + i.quantity * i.unitPrice, 0);

  const updateAddress = (field: keyof AddressModel, value: string) => {
    setFromAddress((prev) => ({
      ...prev,
      [field]: field === "pincode" ? Number(value) || 0 : value,
    }));
  };
  const pickupPincodeCheck = usePincodeCheck({
    pincode: fromAddress.pincode,
    country: isIndianAddress(fromAddress.country) ? "India" : fromAddress.country,
    onFound: (d) =>
      setFromAddress((prev) => ({
        ...prev,
        state:
          matchStateName(d.state, stateData.map((s) => s.name)) ?? d.state,
        city: d.city,
      })),
  });

  const mergeSourceItems = (
    source: ReturnableDCSource,
    existing: DCItemRow[],
  ): DCItemRow[] => {
    const byCode = new Map<string, DCItemRow>();
    existing.forEach((row) => byCode.set(row.itemCode, { ...row }));

    for (const it of source.items) {
      const prev = byCode.get(it.item_code);
      if (prev) {
        prev.deliveredQty += it.delivered_qty;
        if (!prev.sourceOrderIds.includes(source.order_id)) {
          prev.sourceOrderIds.push(source.order_id);
        }
        // keep the user's current qty but clamp to the new (larger) cap
        prev.quantity = Math.min(prev.quantity, prev.deliveredQty);
        if (!prev.priceLocked && (it.price ?? 0) > 0) {
          prev.unitPrice = it.price;
          prev.priceLocked = true;
        }
      } else {
        byCode.set(it.item_code, {
          itemCode: it.item_code,
          itemName: it.item_name,
          quantity: 0,
          unitPrice: it.price ?? 0,
          priceLocked: (it.price ?? 0) > 0,
          itemUnit: it.unit ?? "",
          itemHsn: it.hsn ?? "",
          subcategory: it.subcategory ?? "",
          deliveredQty: it.delivered_qty,
          sourceOrderIds: [source.order_id],
        });
      }
    }
    return Array.from(byCode.values());
  };

  // Lines whose source order carried no price fall back to WAC. Ambill rejects
  // a 0-value DC, so anything still at 0 is left editable for manual entry.
  const fillMissingPrices = async (rows: DCItemRow[]): Promise<DCItemRow[]> => {
    const missing = rows.filter((r) => !r.priceLocked && r.unitPrice <= 0);
    if (!missing.length) return rows;

    const wacByCode = new Map<string, number>();
    await Promise.all(
      missing.map(async (r) => {
        try {
          const res = await GetItemWAC(r.itemCode);
          const value = res?.success ? Number(res?.data?.wac_value) : 0;
          if (value > 0) wacByCode.set(r.itemCode, value);
        } catch {
          // left at 0 → user enters it manually
        }
      }),
    );

    const stillMissing = missing
      .filter((r) => !wacByCode.has(r.itemCode))
      .map((r) => r.itemCode);
    if (stillMissing.length) {
      showToast({
        type: ToastType.warning,
        text: `No price or WAC found for ${stillMissing.join(", ")}. Enter unit price manually.`,
      });
    }

    return rows.map((r) =>
      wacByCode.has(r.itemCode)
        ? { ...r, unitPrice: wacByCode.get(r.itemCode)!, priceLocked: true }
        : r,
    );
  };

  const addOrder = async () => {
    const orderId = orderIdInput.trim();
    if (!orderId) return;
    if (linkedOrderIds.includes(orderId)) {
      showToast({ type: ToastType.warning, text: "Order already added" });
      return;
    }

    try {
      setLoading(true);
      const res = await GetReturnableDCSource(sourceType, orderId);
      if (!res?.success) {
        showToast({
          type: ToastType.error,
          text: res?.error || "Could not fetch order",
        });
        return;
      }

      const source: ReturnableDCSource = res.data;
      if (source.source_order_type !== sourceType) {
        showToast({
          type: ToastType.error,
          text: `Expected a ${sourceType} but got a ${source.source_order_type}`,
        });
        return;
      }
      if (!source.items?.length) {
        showToast({
          type: ToastType.error,
          text: "No delivered items found on this order",
        });
        return;
      }

      const counterpartyNoun =
        sourceType === "PO"
          ? "vendor"
          : sourceType === "ST"
            ? "pickup warehouse"
            : "customer";

      if (linkedOrderIds.length === 0) {
        // First order seeds the editable pickup address + counterparty + the
        // identity that every later order must match.
        if (source.from_address)
          setFromAddress(withResolvedState(source.from_address));
        setCustomerId(source.counterparty?.id ?? "");
        setCustomerName(source.counterparty?.name ?? "");
        setMatchKey(source.match_key ?? "");
        setMatchLabel(source.match_label ?? source.counterparty?.name ?? "");
      } else if ((source.match_key ?? "") !== matchKey) {
        showToast({
          type: ToastType.error,
          text: `All orders must belong to the same ${counterpartyNoun}. Expected ${
            matchLabel || "the same " + counterpartyNoun
          }, but this order belongs to ${
            source.match_label ?? "a different " + counterpartyNoun
          }.`,
        });
        return;
      }

      setItems(await fillMissingPrices(mergeSourceItems(source, items)));
      setLinkedOrderIds((prev) => [...prev, source.order_id]);
      setOrderIdInput("");
      setError("");
    } catch (err: any) {
      showToast({ type: ToastType.error, text: err.message });
    } finally {
      setLoading(false);
    }
  };

  const changeEntryMode = (mode: "order" | "customer") => {
    if (sourceLocked) return;
    setEntryMode(mode);
    if (mode === "customer") {
      setOrderIdInput("");
      // Customer-driven pickups are always raised against Sales Orders.
      if (sourceType !== "SO") changeSourceType("SO");
    } else {
      setSelectedCustomer(undefined);
      setCustomerFromDate(undefined);
      setCustomerToDate(undefined);
    }
  };

  // "By Customer" mode: fetch every delivered SO for the selected customer in
  // the date range and merge them into one consolidated pickup. The backend
  // resolves and merges every order in one request (fetched in parallel
  // server-side), instead of the frontend calling one order at a time.
  const fetchOrdersForCustomer = async () => {
    if (!selectedCustomer?.uid || !customerFromDate || !customerToDate) return;

    try {
      setLoading(true);
      setError("");

      // Full reset on every fetch — re-running on any input change avoids
      // double-counting orders from a previous range.
      setLinkedOrderIds([]);
      setItems([]);
      setMatchKey("");
      setMatchLabel("");
      setFromAddress(emptyAddress());

      const customerUid = String(selectedCustomer.uid);
      const customerFullName = `${selectedCustomer.first_name ?? ""} ${
        selectedCustomer.last_name ?? ""
      }`.trim();
      setCustomerId(customerUid);
      setCustomerName(customerFullName);

      const res = await GetReturnableDCOrdersByCustomerConsolidated(
        customerUid,
        customerFromDate,
        customerToDate,
      );
      if (!res?.success) {
        showToast({
          type: ToastType.error,
          text: res?.error || "Could not fetch orders for this customer",
        });
        return;
      }

      const result: ReturnableDCConsolidatedResult = res.data;
      if (!result.linkedOrderIds.length) {
        showToast({
          type: ToastType.info,
          text: result.items.length
            ? "No delivered items found on any order in this range"
            : "No sales orders found for this customer in the selected range",
        });
        return;
      }

      if (result.fromAddress)
        setFromAddress(withResolvedState(result.fromAddress));
      setMatchKey(result.matchKey ?? "");
      setMatchLabel(result.matchLabel || customerFullName);

      setItems(
        await fillMissingPrices(
          result.items.map(
            (it): DCItemRow => ({
              itemCode: it.item_code,
              itemName: it.item_name,
              quantity: 0,
              unitPrice: it.price ?? 0,
              priceLocked: (it.price ?? 0) > 0,
              itemUnit: it.unit ?? "",
              itemHsn: it.hsn ?? "",
              subcategory: it.subcategory ?? "",
              deliveredQty: it.delivered_qty,
              sourceOrderIds: it.source_order_ids,
            }),
          ),
        ),
      );
      setLinkedOrderIds(result.linkedOrderIds);

      showToast({
        type: ToastType.success,
        text: `Fetched ${result.linkedOrderIds.length} order(s), ${result.items.length} item line(s)${
          result.skipped
            ? ` (${result.skipped} order(s) had nothing delivered)`
            : ""
        }`,
      });
    } catch (err: any) {
      showToast({ type: ToastType.error, text: err.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (
      entryMode === "customer" &&
      selectedCustomer?.uid &&
      customerFromDate &&
      customerToDate
    ) {
      fetchOrdersForCustomer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entryMode, selectedCustomer?.uid, customerFromDate, customerToDate]);

  const removeOrder = (orderId: string) => {
    const remaining = linkedOrderIds.filter((o) => o !== orderId);
    setLinkedOrderIds(remaining);
    if (remaining.length === 0) {
      setItems([]);
      setCustomerId("");
      setCustomerName("");
      setMatchKey("");
      setMatchLabel("");
      setFromAddress(emptyAddress());
    } else {
      // drop lines that only came from the removed order
      setItems((prev) =>
        prev
          .map((row) => ({
            ...row,
            sourceOrderIds: row.sourceOrderIds.filter((o) => o !== orderId),
          }))
          .filter((row) => row.sourceOrderIds.length > 0),
      );
    }
  };

  const removeItem = (i: number) =>
    setItems((p) => p.filter((_, x) => x !== i));

  const handleCreate = async () => {
    if (!linkedOrderIds.length)
      return setError("Add at least one source order");
    if (!items.length) return setError("No items to pick up");
    if (items.some((i) => i.quantity <= 0))
      return setError("All items must have quantity greater than 0");
    if (items.some((i) => i.quantity > i.deliveredQty))
      return setError("Quantity cannot exceed the delivered quantity");
    const zeroPriced = items.filter((i) => !(i.unitPrice > 0));
    if (zeroPriced.length)
      return setError(
        `Unit price must be greater than 0 for: ${zeroPriced
          .map((i) => i.itemCode)
          .join(", ")}`,
      );
    if (!toWarehouse)
      return setError("Select the destination (Kazam) warehouse");
    if (!customerName.trim()) return setError("Enter the pickup location name");
    const fromAddressIsIndia =
      fromAddress.country?.trim().toLowerCase() === "india";
    if (
      !fromAddress.line1?.trim() ||
      !fromAddress.city?.trim() ||
      (fromAddressIsIndia && !fromAddress.state?.trim()) ||
      (fromAddressIsIndia && !fromAddress.pincode)
    )
      return setError(
        fromAddressIsIndia
          ? "Enter the complete pickup (from) address — line 1, city, state and pincode are required"
          : "Enter the complete pickup (from) address — line 1 and city are required",
      );
    const pickupPincodeError = pickupPincodeCheck.validate(fromAddress.state);
    if (pickupPincodeError)
      return setError(`Pickup address: ${pickupPincodeError}`);
    if (!contactName.trim()) return setError("Enter contact name");
    if (contactNumber.replace(/\D/g, "").length < 10)
      return setError("Contact number must be at least 10 digits");
    if (!purpose.trim()) return setError("Enter purpose");
    if (!description.trim()) return setError("Enter description");

    const payload: CreateReturnableDCPayload = {
      source_order_type: sourceType,
      linked_order_ids: linkedOrderIds,
      customer_id: customerId || undefined,
      customer_name: customerName || undefined,
      shipping_address: fromAddress,
      to_warehouse_id: toWarehouse.id,
      to_warehouse_name: toWarehouse.name,
      contact_name: contactName,
      contact_number: contactNumber,
      purpose,
      description,
      items: items.map(
        (i): ItemWarehouseModel => ({
          inventoryItemCode: i.itemCode,
          itemName: i.itemName,
          requestedQty: i.quantity,
          itemPrice: i.unitPrice,
          subCategory: i.subcategory,
          itemHSN: i.itemHsn,
          itemUnit: i.itemUnit,
          source_order_id: i.sourceOrderIds.join(","),
          tax_detail: { igst: 0, cgst: 0, sgst: 0 },
          gst_applicable: false,
        }),
      ),
      shipping_details: {
        logistic_partner: logisticPartner.trim() || undefined,
        tracking_id: trackingId.trim() || undefined,
      },
    };

    try {
      setLoading(true);
      const res = await CreateReturnableDC(payload);
      if (!res?.success) {
        showToast({
          type: ToastType.error,
          text: res?.error || "Reverse DC creation failed",
        });
        return;
      }
      showToast({
        type: ToastType.success,
        text: "Reverse DC created successfully",
      });
      history.goBack();
    } catch (err: any) {
      console.error(err);
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      id="warehouse-stock-transfer-returnable-dc-form"
      className="p-6 space-y-6"
    >
      {/* Entry mode: type in individual order ids, or pick a customer + date range */}
      <div className="space-y-2">
        <label className="text-sm font-medium">
          Raise Against <RequiredMark />
        </label>
        <div className="flex flex-wrap gap-2">
          <button
            id="warehouse-stock-transfer-returnable-dc-form-entry-mode-order"
            type="button"
            disabled={sourceLocked && entryMode !== "order"}
            onClick={() => changeEntryMode("order")}
            className={`px-3 py-1.5 rounded-md border text-sm transition-all ${
              entryMode === "order"
                ? "border-blue-600 bg-blue-50 text-blue-700"
                : "border-gray-200 hover:border-blue-400"
            } ${
              sourceLocked && entryMode !== "order"
                ? "opacity-40 cursor-not-allowed"
                : ""
            }`}
          >
            By Order ID
          </button>
          <button
            id="warehouse-stock-transfer-returnable-dc-form-entry-mode-customer"
            type="button"
            disabled={sourceLocked && entryMode !== "customer"}
            onClick={() => changeEntryMode("customer")}
            className={`px-3 py-1.5 rounded-md border text-sm transition-all ${
              entryMode === "customer"
                ? "border-blue-600 bg-blue-50 text-blue-700"
                : "border-gray-200 hover:border-blue-400"
            } ${
              sourceLocked && entryMode !== "customer"
                ? "opacity-40 cursor-not-allowed"
                : ""
            }`}
          >
            By Customer
          </button>
          {sourceLocked && (
            <span className="text-xs text-gray-500 self-center">
              Remove all orders to change mode
            </span>
          )}
        </div>
      </div>

      {entryMode === "order" ? (
        <>
          {/* Source order type + add */}
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2">
              {SOURCE_TYPES.map((t) => (
                <button
                  key={t.key}
                  id={`warehouse-stock-transfer-returnable-dc-form-source-type-${t.key.toLowerCase()}`}
                  type="button"
                  disabled={sourceLocked && sourceType !== t.key}
                  onClick={() => changeSourceType(t.key)}
                  className={`px-3 py-1.5 rounded-md border text-sm transition-all ${
                    sourceType === t.key
                      ? "border-blue-600 bg-blue-50 text-blue-700"
                      : "border-gray-200 hover:border-blue-400"
                  } ${
                    sourceLocked && sourceType !== t.key
                      ? "opacity-40 cursor-not-allowed"
                      : ""
                  }`}
                >
                  {t.label}
                </button>
              ))}
              {sourceLocked && (
                <span className="text-xs text-gray-500 self-center">
                  Remove all orders to change type
                </span>
              )}
            </div>
          </div>

          <div className="flex gap-2">
            <input
              id="warehouse-stock-transfer-returnable-dc-form-order-id-input"
              type="text"
              value={orderIdInput}
              onChange={(e) => setOrderIdInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addOrder()}
              className="flex-1 h-8 border rounded-md px-3"
              placeholder={`Enter ${sourceType} id and add`}
            />
            <button
              id="warehouse-stock-transfer-returnable-dc-form-add-order-btn"
              onClick={addOrder}
              disabled={loading}
              className={`px-4 bg-blue-600 text-white rounded-md flex items-center transition-all ${
                loading ? "opacity-50 cursor-not-allowed" : "hover:bg-blue-700"
              }`}
            >
              <Icon path={mdiPlus} size={0.8} />
            </button>
          </div>
        </>
      ) : (
        <div className="space-y-2">
          <label className="text-sm font-medium">
            Customer <RequiredMark />
          </label>
          <UserSearchFieldV2
            value={selectedCustomer as any}
            placeholder="Search customer"
            onSelect={setSelectedCustomer}
          />
          <label className="text-sm font-medium block mt-2">
            Date Range <RequiredMark />
          </label>
          <DateRangeFilter
            compact
            showChips={false}
            fromDate={customerFromDate}
            toDate={customerToDate}
            onFromChange={setCustomerFromDate}
            onToChange={setCustomerToDate}
          />
          <p className="text-xs text-gray-500">
            All Sales Orders delivered to this customer in the selected range
            will be fetched and consolidated below.
          </p>
        </div>
      )}

      {linkedOrderIds.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {linkedOrderIds.map((o) => (
            <span
              key={o}
              id={`warehouse-stock-transfer-returnable-dc-form-linked-order-${o}`}
              className="inline-flex items-center gap-1 bg-gray-100 px-2 py-1 rounded-md text-xs"
            >
              {o}
              <button
                id={`warehouse-stock-transfer-returnable-dc-form-linked-order-${o}-remove-btn`}
                onClick={() => removeOrder(o)}
                className="text-red-600"
                title="Remove order"
              >
                <Icon path={mdiTrashCanOutline} size={0.6} />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Destination warehouse + dates */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium">
            To Warehouse (Kazam) <RequiredMark />
          </label>
          <WarehouseSearchField
            id="warehouse-stock-transfer-returnable-dc-form-to-warehouse"
            type="internal"
            value={toWarehouse || undefined}
            onSelect={(w) => setToWarehouse(w)}
            onClear={() => setToWarehouse(null)}
          />
          {toWarehouse?.address && (
            <p className="text-xs text-gray-500 mt-1">
              Billing address will be drawn from this warehouse&apos;s GST
              registration ({toWarehouse.address.city},{" "}
              {toWarehouse.address.state}).
            </p>
          )}
        </div>
      </div>

      {/* From (pickup) address — editable */}
      <div className="space-y-2">
        <label className="text-sm font-medium">
          Pickup (From) Address <RequiredMark />
        </label>
        <input
          id="warehouse-stock-transfer-returnable-dc-form-pickup-name"
          type="text"
          value={customerName}
          onChange={(e) => setCustomerName(e.target.value)}
          className="w-full h-8 border rounded-md px-3 mb-3"
          placeholder="Pickup location name (person / business / warehouse) *"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input
            id="warehouse-stock-transfer-returnable-dc-form-pickup-line1"
            type="text"
            value={fromAddress.line1 ?? ""}
            onChange={(e) => updateAddress("line1", e.target.value)}
            className="h-8 border rounded-md px-3"
            placeholder="Address line 1 *"
          />
          <input
            id="warehouse-stock-transfer-returnable-dc-form-pickup-line2"
            type="text"
            value={fromAddress.line2 ?? ""}
            onChange={(e) => updateAddress("line2", e.target.value)}
            className="h-8 border rounded-md px-3"
            placeholder="Address line 2"
          />
          <input
            id="warehouse-stock-transfer-returnable-dc-form-pickup-city"
            type="text"
            value={fromAddress.city ?? ""}
            onChange={(e) => updateAddress("city", e.target.value)}
            className="h-8 border rounded-md px-3"
            placeholder="City *"
          />
          {isIndianAddress(fromAddress.country) ? (
            <select
              id="warehouse-stock-transfer-returnable-dc-form-pickup-state"
              value={fromAddress.state ?? ""}
              onChange={(e) => updateAddress("state", e.target.value)}
              className="h-8 border rounded-md px-3"
            >
              <option value="">State *</option>
              {stateData.map((state) => (
                <option key={state.id} value={state.name}>
                  {state.name}
                </option>
              ))}
            </select>
          ) : (
            <input
              id="warehouse-stock-transfer-returnable-dc-form-pickup-state"
              type="text"
              value={fromAddress.state ?? ""}
              onChange={(e) => updateAddress("state", e.target.value)}
              className="h-8 border rounded-md px-3"
              placeholder="State *"
            />
          )}
          <input
            id="warehouse-stock-transfer-returnable-dc-form-pickup-country"
            type="text"
            value={fromAddress.country ?? ""}
            onChange={(e) => updateAddress("country", e.target.value)}
            className="h-8 border rounded-md px-3"
            placeholder="Country"
          />
          <div>
            <input
              id="warehouse-stock-transfer-returnable-dc-form-pickup-pincode"
              type="number"
              value={fromAddress.pincode || ""}
              onChange={(e) => {
                pickupPincodeCheck.markTyped();
                updateAddress("pincode", e.target.value);
              }}
              className="h-8 w-full border rounded-md px-3"
              placeholder="Pincode *"
            />
            <PincodeHint check={pickupPincodeCheck} />
          </div>
        </div>
      </div>

      {/* Contact */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium">
            Contact Name <RequiredMark />
          </label>
          <input
            id="warehouse-stock-transfer-returnable-dc-form-contact-name"
            type="text"
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            className="w-full h-8 border rounded-md px-3"
          />
        </div>
        <div>
          <label className="text-sm font-medium">
            Contact Number <RequiredMark />
          </label>
          <input
            id="warehouse-stock-transfer-returnable-dc-form-contact-number"
            type="text"
            value={contactNumber}
            onChange={(e) => setContactNumber(e.target.value)}
            className="w-full h-8 border rounded-md px-3"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium">
            Purpose <RequiredMark />
          </label>
          <input
            id="warehouse-stock-transfer-returnable-dc-form-purpose"
            type="text"
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
            className="w-full h-8 border rounded-md px-3"
          />
        </div>
        <div>
          <label className="text-sm font-medium">
            Description <RequiredMark />
          </label>
          <input
            id="warehouse-stock-transfer-returnable-dc-form-description"
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full h-8 border rounded-md px-3"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium">Logistic Partner</label>
          <input
            id="warehouse-stock-transfer-returnable-dc-form-logistic-partner"
            type="text"
            value={logisticPartner}
            onChange={(e) => setLogisticPartner(e.target.value)}
            className="w-full h-8 border rounded-md px-3"
            placeholder="Enter logistic partner"
          />
        </div>
        <div>
          <label className="text-sm font-medium">Tracking ID</label>
          <input
            id="warehouse-stock-transfer-returnable-dc-form-tracking-id"
            type="text"
            value={trackingId}
            onChange={(e) => setTrackingId(e.target.value)}
            className="w-full h-8 border rounded-md px-3"
            placeholder="Enter tracking ID"
          />
        </div>
      </div>

      {/* Items (from source orders) */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold">Item Details</h2>

        <div className="space-y-3">
          {items.map((item, index) => (
            <div
              key={item.itemCode}
              id={`warehouse-stock-transfer-returnable-dc-form-item-row-${item.itemCode}`}
              className="p-4 border rounded-xl bg-gray-50 space-y-3"
            >
              <div className="flex items-center gap-2 font-medium">
                <span>
                  {item.itemName} - {item.itemCode}
                </span>
                <span
                  className={`text-[10px] px-2 py-0.5 rounded-full ${
                    isFinishedGoods(item.subcategory)
                      ? "bg-purple-100 text-purple-700"
                      : "bg-amber-100 text-amber-700"
                  }`}
                >
                  {isFinishedGoods(item.subcategory) ? "FG" : "RM"}
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-sm font-medium">
                    Quantity <RequiredMark />
                  </label>
                  <input
                    id={`warehouse-stock-transfer-returnable-dc-form-item-row-${item.itemCode}-quantity`}
                    type="number"
                    min={1}
                    max={item.deliveredQty}
                    value={item.quantity}
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      if (value > item.deliveredQty) return;
                      setItems((prev) =>
                        prev.map((it, i) =>
                          i === index ? { ...it, quantity: value } : it,
                        ),
                      );
                    }}
                    className="h-10 w-full border rounded-md px-3"
                    placeholder="Quantity"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium">
                    Unit Price <RequiredMark />
                  </label>
                  <input
                    id={`warehouse-stock-transfer-returnable-dc-form-item-row-${item.itemCode}-unit-price`}
                    type="number"
                    min={0}
                    value={item.unitPrice}
                    readOnly={item.priceLocked}
                    tabIndex={item.priceLocked ? -1 : undefined}
                    onChange={(e) => {
                      if (item.priceLocked) return;
                      const value = Number(e.target.value);
                      setItems((prev) =>
                        prev.map((it, i) =>
                          i === index ? { ...it, unitPrice: value } : it,
                        ),
                      );
                    }}
                    className={`h-10 w-full border rounded-md px-3 ${
                      item.priceLocked
                        ? "bg-gray-100 text-gray-600 cursor-not-allowed"
                        : item.unitPrice > 0
                          ? ""
                          : "border-red-500"
                    }`}
                    placeholder="Unit Price"
                  />
                  {!item.priceLocked && (
                    <p className="text-xs text-amber-700">
                      No price on the source order or WAC — enter manually.
                    </p>
                  )}
                </div>
              </div>

              <div className="flex justify-between items-center">
                <p className="text-xs text-green-700 font-medium">
                  Delivered (max): {item.deliveredQty}
                </p>
                <button
                  id={`warehouse-stock-transfer-returnable-dc-form-item-row-${item.itemCode}-remove-btn`}
                  onClick={() => removeItem(index)}
                  className="text-red-600"
                >
                  <Icon path={mdiTrashCanOutline} size={0.8} />
                </button>
              </div>
            </div>
          ))}

          {items.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-6">
              Add a source order to load its delivered items
            </p>
          )}
        </div>
      </div>

      {error && (
        <p className="text-sm text-red-600 bg-red-50 p-3 rounded-md">{error}</p>
      )}

      <div className="border-t px-6 py-4 flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
        <div className="font-semibold">
          Total: ₹{totalValue.toLocaleString()}
        </div>
        <div className="flex gap-3">
          <button
            id="warehouse-stock-transfer-returnable-dc-form-cancel-btn"
            onClick={() => history.goBack()}
            disabled={loading}
            className="border px-6 py-2 rounded-lg disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            id="warehouse-stock-transfer-returnable-dc-form-submit-btn"
            onClick={handleCreate}
            disabled={loading}
            className={`bg-blue-600 text-white px-6 py-2 rounded-lg transition-all ${
              loading ? "opacity-50 cursor-not-allowed" : "hover:bg-blue-700"
            }`}
          >
            {loading ? "Creating..." : "Create Reverse DC"}
          </button>
        </div>
      </div>

      {loading && <LoadingWidget />}
    </div>
  );
}
